import React from 'react'

import { Color, Link, LinkProps, LinkVariant, withCitrusTheme } from '@gousto-internal/citrus-react'

export type CWCLinkButtonVariant = 'primary' | 'secondary'

export type CWCLinkButtonProps = {
  href: string
  children: React.ReactNode
  /**
   * Receives the click event so signup-bound CTAs can `preventDefault()` the raw anchor
   * navigation and dispatch the promo/redirect flow instead (see CWCWhatsNewSection). CTAs to
   * other pages can ignore the event and just navigate via `href` while tracking the click.
   */
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void
  /**
   * `primary` (default) = solid purple fill (CWCHero CTA). `secondary` = transparent fill
   * with a 2px purple outline and purple text (CwC Suppliers CTA).
   */
  variant?: CWCLinkButtonVariant
}

/**
 * A navigational CTA that looks like a Citrus button but is a real anchor (`<a href>`)
 * under the hood — Citrus has no link-styled-as-button primitive, and elements that
 * navigate to another page should be links, not buttons.
 *
 * Built on the Citrus `Link`, so its typography is inherited; we only override the button
 * appearance (fill/outline, radius, padding, text colour, hover/focus). Both variants use
 * the `Secondary_400` token (#615CFF) and an 8px radius. Full-width on mobile, a wider fixed
 * width from tablet up, matching the design.
 *
 * The visual variant travels as `buttonVariant`, stripped before it reaches the DOM (mirrors
 * `InlineLink`), so it never collides with Citrus `Link`'s own `variant` prop.
 */
type StyledLinkProps = LinkProps & { buttonVariant?: CWCLinkButtonVariant }

const StyledLink = withCitrusTheme<StyledLinkProps>(
  (props: StyledLinkProps) => {
    const { buttonVariant, ...linkProps } = props

    return <Link {...linkProps} />
  },
  (theme, props: StyledLinkProps) => {
    const isSecondary = props.buttonVariant === 'secondary'

    return [
      {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '48px',
        padding: '10px 24px',
        borderRadius: '8px',
        fontFamily: theme.tokens.FontFamilies.bold,
        textDecoration: 'none',
        // 16px constant. Citrus Link with variant=None renders bare children (no Link1 wrapper), so
        // its `size` prop is ignored and the text would otherwise inherit the theme's ramping body
        // default (16→18 at >=768). A base fontSize on the anchor overrides that inherited value at
        // every breakpoint — and there's no competing same-element Citrus media rule to lose to.
        fontSize: '16px',
        backgroundColor: isSecondary ? 'transparent' : theme.color(Color.Secondary_400),
        color: isSecondary ? theme.color(Color.Secondary_400) : theme.color(Color.White),
        border: isSecondary ? `2px solid ${theme.color(Color.Secondary_400)}` : 'none',
        ':hover': {
          backgroundColor: isSecondary
            ? theme.color(Color.Secondary_50)
            : theme.color(Color.Secondary_600),
          color: isSecondary ? theme.color(Color.Secondary_600) : theme.color(Color.White),
        },
        ':focus-visible': {
          outline: `1px solid ${theme.color(Color.Secondary_600)}`,
          outlineOffset: '2px',
        },
      },
      theme.responsiveStyle(['100%', null, '345px'], ['width']),
    ]
  },
)

export const CWCLinkButton = ({
  href,
  children,
  onClick,
  variant = 'primary',
}: CWCLinkButtonProps) => {
  // A real <a href> is already keyboard-accessible: it's in the tab order (Tab) and activates on
  // Enter natively. Anchors do NOT activate on Space (Space scrolls the page), so — because this link
  // is styled as a button — add Space activation for button-consistent keyboard operability. Arrow
  // keys keep their default (page scroll); they are not activation keys for a link or a button.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
    if (event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault()
      event.currentTarget.click()
    }
  }

  return (
    <StyledLink
      href={href}
      variant={LinkVariant.None}
      buttonVariant={variant}
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      {children}
    </StyledLink>
  )
}
